import { useCallback, useEffect, useMemo, useState } from 'react'
import { Check, Copy, Loader2, RotateCcw, Zap } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { useAuthStore } from '@/shared/stores/auth'
import { clearStoredReferralCode, getStoredReferralCode } from '../lib/referral'
import { usePublicKeyAuthFlow } from '../hooks/usePublicKeyAuthFlow'
import type { AuthResponse, PublicKeyAuthAction, PublicKeyAuthMethod } from '../types'

interface PublicKeyAuthDialogProps {
  action: PublicKeyAuthAction
  method: PublicKeyAuthMethod
  onClose: () => void
  onSuccess?: () => void
  open: boolean
  switchToSignin: () => void
  switchToSignup: () => void
}

function methodLabel(method: PublicKeyAuthMethod): string {
  return method === 'lnurl' ? 'Lightning' : 'Auth47 paynym'
}

function titleFor(method: PublicKeyAuthMethod, action: PublicKeyAuthAction): string {
  if (action === 'link') return `Link ${methodLabel(method)}`
  if (action === 'signup') return `Sign up with ${methodLabel(method)}`
  return `Sign in with ${methodLabel(method)}`
}

function descriptionFor(method: PublicKeyAuthMethod, action: PublicKeyAuthAction): string {
  const wallet = method === 'lnurl' ? 'Lightning wallet' : 'Auth47-compatible wallet'
  if (action === 'link') return `Scan this code to add ${methodLabel(method)} to your account.`
  if (action === 'signup') return `Scan this code with your ${wallet} to create an account.`
  return `Scan this code with your ${wallet} to access your account.`
}

function qrValue(method: PublicKeyAuthMethod, payload: string): string {
  return method === 'lnurl' ? `lightning:${payload}` : payload
}

function codeLabel(action: PublicKeyAuthAction): string {
  if (action === 'link') return 'link code'
  if (action === 'signup') return 'sign-up code'
  return 'sign-in code'
}

export function PublicKeyAuthDialog({
  action,
  method,
  onClose,
  onSuccess,
  open,
  switchToSignin,
  switchToSignup,
}: PublicKeyAuthDialogProps) {
  const { checkSession, setToken, setUser } = useAuthStore()
  const [copied, setCopied] = useState(false)

  const completeAuth = useCallback(
    async (auth: AuthResponse) => {
      setToken(auth.refreshToken)
      setUser(auth.user)
      clearStoredReferralCode()
      onClose()
      onSuccess?.()
    },
    [onClose, onSuccess, setToken, setUser]
  )

  const completeLink = useCallback(async () => {
    await checkSession()
    onClose()
    onSuccess?.()
  }, [checkSession, onClose, onSuccess])

  const { challenge, copyValue, error, isLoading, isPolling, reset } = usePublicKeyAuthFlow({
    action,
    enabled: open,
    method,
    onAuthComplete: completeAuth,
    onLinkComplete: completeLink,
    referralCode: action === 'signup' ? getStoredReferralCode() : null,
  })

  useEffect(() => {
    setCopied(false)
  }, [challenge?.uuid, open])

  const displayedQrValue = useMemo(
    () => (challenge ? qrValue(method, challenge.payload) : ''),
    [challenge, method]
  )
  const authCodeLabel = codeLabel(action)

  const copyPayload = async () => {
    if (!copyValue) return
    try {
      await navigator.clipboard.writeText(copyValue)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="!bottom-0 !left-0 !right-0 !top-auto !max-h-[92dvh] !translate-x-0 !translate-y-0 overflow-y-auto rounded-none border-x-0 border-b-0 sm:!bottom-auto sm:!left-[50%] sm:!right-auto sm:!top-[50%] sm:!max-h-[85dvh] sm:!translate-x-[-50%] sm:!translate-y-[-50%] sm:max-w-sm sm:rounded-lg sm:border">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Zap className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle>{titleFor(method, action)}</DialogTitle>
          <DialogDescription>{descriptionFor(method, action)}</DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-5">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error.message}
            </div>
          )}

          <div className="flex min-h-[17rem] items-center justify-center">
            {isLoading ? (
              <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                Generating secure {authCodeLabel}...
              </div>
            ) : challenge ? (
              <div className="space-y-4 text-center">
                <a
                  className="mx-auto block w-fit rounded-xl bg-white p-4 shadow-sm"
                  href={displayedQrValue}
                >
                  <QRCodeSVG
                    bgColor="#ffffff"
                    fgColor="#111111"
                    size={224}
                    value={displayedQrValue}
                  />
                </a>
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  {isPolling && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                  {isPolling
                    ? 'Waiting for wallet confirmation...'
                    : 'Scan this code with your wallet.'}
                </div>
              </div>
            ) : (
              <div className="text-center text-sm text-muted-foreground">
                Unable to generate a {authCodeLabel}.
              </div>
            )}
          </div>

          {challenge && (
            <div className="rounded-md border border-border bg-muted/30 p-2 text-xs text-muted-foreground">
              <div className="line-clamp-2 break-all font-mono">{copyValue}</div>
            </div>
          )}

          <div className="space-y-2">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => void copyPayload()}
              disabled={!copyValue}
            >
              {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
              {copied ? 'Copied' : 'Copy code'}
            </Button>
            <Button variant="ghost" className="w-full" onClick={reset} disabled={isLoading}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Generate new code
            </Button>
          </div>

          {action !== 'link' && (
            <p className="text-center text-sm text-muted-foreground">
              {action === 'signup' ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button
                type="button"
                onClick={action === 'signup' ? switchToSignin : switchToSignup}
                className="border-none bg-transparent p-0 text-primary hover:underline"
              >
                {action === 'signup' ? 'Sign in' : 'Sign up'}
              </button>
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
